import React from 'react';
import {
  AbsoluteFill,
  interpolate,
  spring,
  useCurrentFrame,
  useVideoConfig,
} from 'remotion';
import {TrendingUp, Zap} from 'lucide-react';
import {brand, brandFont} from '../brand';
import {BgGradient} from '../components/BgGradient';
import {EyebrowPill} from '../components/EyebrowPill';

export type StatsRevealProps = {
  eyebrow: string;
  statValue: number;
  statSuffix: string;
  statLabel: string;
  comparison: {label: string; value: string};
  footer: string;
};

export const StatsReveal: React.FC<StatsRevealProps> = ({
  eyebrow,
  statValue,
  statSuffix,
  statLabel,
  comparison,
  footer,
}) => {
  const frame = useCurrentFrame();
  const {fps} = useVideoConfig();

  const headerOpacity = interpolate(frame, [0, 14], [0, 1], {
    extrapolateLeft: 'clamp',
    extrapolateRight: 'clamp',
  });

  const counterSpring = spring({
    frame: Math.max(0, frame - 14),
    fps,
    from: 0,
    to: 1,
    config: {damping: 20, stiffness: 70, mass: 1.4},
  });

  const counterValue = counterSpring * statValue;
  const display =
    statValue % 1 === 0
      ? Math.round(counterValue).toString()
      : counterValue.toFixed(1);

  const statScale = interpolate(counterSpring, [0, 1], [0.6, 1]);
  const statOpacity = interpolate(frame, [14, 32], [0, 1], {
    extrapolateLeft: 'clamp',
    extrapolateRight: 'clamp',
  });

  const labelOpacity = interpolate(frame, [40, 60], [0, 1], {
    extrapolateLeft: 'clamp',
    extrapolateRight: 'clamp',
  });

  const comparisonOpacity = interpolate(frame, [80, 110], [0, 1], {
    extrapolateLeft: 'clamp',
    extrapolateRight: 'clamp',
  });
  const comparisonY = interpolate(frame, [80, 110], [40, 0], {
    extrapolateLeft: 'clamp',
    extrapolateRight: 'clamp',
  });

  const footerOpacity = interpolate(frame, [140, 170], [0, 1], {
    extrapolateLeft: 'clamp',
    extrapolateRight: 'clamp',
  });

  return (
    <AbsoluteFill>
      <BgGradient />

      <AbsoluteFill
        style={{
          padding: '180px 60px 120px',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 50,
        }}
      >
        <div style={{opacity: headerOpacity}}>
          <EyebrowPill label={eyebrow} />
        </div>

        <div
          style={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 24,
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'baseline',
              gap: 12,
              transform: `scale(${statScale})`,
              opacity: statOpacity,
            }}
          >
            <div
              style={{
                fontFamily: brandFont,
                fontSize: 360,
                fontWeight: 900,
                letterSpacing: -16,
                lineHeight: 0.9,
                fontVariantNumeric: 'tabular-nums',
                background: `linear-gradient(135deg, ${brand.primaryLight} 0%, ${brand.action} 100%)`,
                WebkitBackgroundClip: 'text',
                backgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                textShadow: '0 12px 60px rgba(0,0,0,0.6)',
              }}
            >
              {display}
            </div>
            <div
              style={{
                fontFamily: brandFont,
                fontSize: 120,
                fontWeight: 800,
                color: brand.textPrimary,
                letterSpacing: -3,
              }}
            >
              {statSuffix}
            </div>
          </div>

          <div
            style={{
              opacity: labelOpacity,
              fontFamily: brandFont,
              fontSize: 44,
              fontWeight: 700,
              color: brand.textPrimary,
              letterSpacing: -0.5,
              textAlign: 'center',
              whiteSpace: 'pre-line',
              maxWidth: 880,
              lineHeight: 1.15,
            }}
          >
            {statLabel}
          </div>
        </div>

        <ComparisonCard
          label={comparison.label}
          value={comparison.value}
          opacity={comparisonOpacity}
          y={comparisonY}
        />

        <div
          style={{
            opacity: footerOpacity,
            fontFamily: brandFont,
            fontSize: 30,
            fontWeight: 600,
            color: brand.textSecondary,
            textAlign: 'center',
            letterSpacing: 0.5,
          }}
        >
          {footer}
        </div>
      </AbsoluteFill>
    </AbsoluteFill>
  );
};

const ComparisonCard: React.FC<{
  label: string;
  value: string;
  opacity: number;
  y: number;
}> = ({label, value, opacity, y}) => (
  <div
    style={{
      opacity,
      transform: `translateY(${y}px)`,
      width: '100%',
      maxWidth: 760,
      padding: 28,
      borderRadius: 28,
      background: brand.card,
      border: `1.5px solid ${brand.border}`,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: 20,
    }}
  >
    <div style={{display: 'flex', alignItems: 'center', gap: 18}}>
      <div
        style={{
          width: 64,
          height: 64,
          borderRadius: 18,
          background: `${brand.success}22`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <TrendingUp size={32} color={brand.success} strokeWidth={2.6} />
      </div>
      <div
        style={{
          fontFamily: brandFont,
          fontSize: 28,
          fontWeight: 600,
          color: brand.textPrimary,
        }}
      >
        {label}
      </div>
    </div>
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        fontFamily: brandFont,
        fontSize: 36,
        fontWeight: 800,
        color: brand.success,
        letterSpacing: -0.5,
        fontVariantNumeric: 'tabular-nums',
      }}
    >
      <Zap size={28} color={brand.success} strokeWidth={2.6} fill={brand.success} />
      {value}
    </div>
  </div>
);
